import {EventSubscriber, HasEventName} from "@/modules/base/events";
import {inject, injectable} from "inversify";
import {AfterCreateGameEvent} from "@/modules/base/events/AfterCreateGameEvent";
import {Player, Team} from "@/modules/team/entity";
import MarketValueCalculator from "@/modules/team/services/MarketValueCalculator";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import ListingHandler from "@/modules/transfer-market/services/ListingHandler";
import chance from "@/modules/base/services/chance";
import OfferCreator from "@/modules/transfer-market/services/OfferCreator";
import {AfterPlayerCreatedEvent} from "@/modules/team/events/AfterPlayerCreatedEvent";
import TransferLogFactory from "@/modules/transfer-market/services/factories/TransferLogFactory";
import {Notifier} from "@/modules/notifications/services/Notifier";
import {TransferOfferNotification} from "@/modules/transfer-market/entity/TransferOfferNotification";
import {AfterDayEvaluationEvent} from "@/modules/season/events/AfterDayEvaluationEvent";
import {WeekdayEnum} from "@/modules/base/enums";
import {TransferListing} from "@/modules/transfer-market/entity";

@injectable()
export class GameCreationEventSubscriber implements EventSubscriber {
  constructor(
    @inject(ConfigRepository) private configRepository: ConfigRepository,
    @inject(MarketValueCalculator) private marketValueCalculator: MarketValueCalculator,
    @inject(ListingHandler) private listingHandler: ListingHandler,
    @inject(OfferCreator) private offerCreator: OfferCreator,
    @inject(TransferLogFactory) private transferLogFactory: TransferLogFactory,
    @inject(Notifier) private notifier: Notifier,
  ) {
  }

  priority(): number {
    return 0;
  }

  init(): { event: HasEventName; callback: (event: any) => void }[] {
    return [
      {event: AfterPlayerCreatedEvent, callback: this.onAfterPlayerCreatedEvent},
      {event: AfterCreateGameEvent, callback: this.onAfterCreateGameEvent},
      {event: AfterDayEvaluationEvent, callback: this.onAfterDayEvaluationEvent},
    ];
  }

  async onAfterPlayerCreatedEvent(event: AfterPlayerCreatedEvent) {
    if (!this.configRepository.get('debug')) {
      return;
    }

    await times(chance.integer({min: 1, max: 3}))
      .forEachAsync(() => this.transferLogFactory.create(event.player));
  }

  async onAfterDayEvaluationEvent(event: AfterDayEvaluationEvent): Promise<void> {
    if (event.season.currentDate.weekday() !== WeekdayEnum.TUESDAY) {
      return;
    }

    console.time('GameCreationEventSubscriber::onAfterDayEvaluationEvent');

    let listingCount = await TransferListing
      .createQueryBuilder('listing')
      .getCount();

    const teamCount = await Team
      .createQueryBuilder('team')
      .getCount();

    const listingTargetCount = this.configRepository.get('transferMarket.listings.targetCount')(listingCount, teamCount);
    const removeCount = this.configRepository.get('transferMarket.listings.removeCount')(listingCount, teamCount);

    const listings = await TransferListing
      .createQueryBuilder('listing')
      .leftJoinAndSelect('listing.offers', 'offer')
      .leftJoinAndSelect('offer.team', 'offerTeam')
      .leftJoinAndSelect('offer.playerNegotiation', 'playerNegotiation')
      .leftJoinAndSelect('offer.teamNegotiation', 'teamNegotiation')
      .innerJoinAndSelect('listing.player', 'player')
      .innerJoinAndSelect('player.team', 'team')
      .where('team.user = false')
      .orderBy("RANDOM()")
      .limit(removeCount)
      .getMany();

    await listings.forEachAsync(async (listing: TransferListing) => {
      await this.listingHandler.remove(listing);
    });

    // creating new listings
    listingCount = await TransferListing
      .createQueryBuilder('listing')
      .getCount();

    const createCount = listingTargetCount - listingCount;

    if (createCount < 1) {
      return;
    }

    const players = await Player
      .createQueryBuilder('player')
      .innerJoin('player.team', 'team')
      .where('team.user = false')
      .orderBy("RANDOM()")
      .take(createCount)
      .getMany();
    console.log('test-create', players);

    await players.forEachAsync(async (p: Player) => {
      await this.listingHandler.create(p, await this.marketValueCalculator.calculate(p));
    });

    console.timeEnd('GameCreationEventSubscriber::onAfterDayEvaluationEvent');
  }

  async onAfterCreateGameEvent(event: AfterCreateGameEvent): Promise<void> {
    const teamCount = await Team
      .createQueryBuilder('team')
      .getCount();

    const listingTargetCount = this.configRepository.get('transferMarket.listings.targetCount')(0, teamCount);

    const players = await Player.createQueryBuilder('player')
      .innerJoin('player.team', 'team')
      .where('team.user != true')
      .orderBy("RANDOM()")
      .take(listingTargetCount)
      .getMany();

    await players.forEachAsync((player: Player) => this.listingHandler.create(player));

    const ownPlayers = await Player.createQueryBuilder('player')
      .innerJoin('player.team', 'team')
      .where('team.user = true')
      .orderBy("RANDOM()")
      .take(7)
      .getMany();

    await ownPlayers
      .forEachAsync(async (player: Player) => {
        const notification = new TransferOfferNotification().setPayload(player);
        notification.createdAt = chance.date();

        await this.notifier.notify(notification);
      });

    return; // todo

    const teamPlayers = await Player
      .createQueryBuilder('player')
      .innerJoinAndSelect('player.team', 'team')
      .where('team.user = true')
      .take(3)
      .getMany();

    await teamPlayers.forEachAsync(async (player: Player) => {
      const listing = await this.listingHandler.create(player);

      const teams = await Team
        .createQueryBuilder('team')
        .where('team.user = false')
        .orderBy('RANDOM()')
        .take(chance.integer({min: 2, max: 3}))
        .getMany();

      await teams.forEachAsync(async (team: Team) => {
        await this.offerCreator.create(
          player,
          team,
          chance.integer({min: 90, max: 110}) * listing.price / 100
        );
      });
    });
  }
}